import React, { useState, useEffect } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { ChartLineUp, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/formatMoney';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const OrbitCommissionsPage = () => {
  const { language } = useLanguage();
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const t = (fr, en) => language === 'fr' ? fr : en;

  useEffect(() => { fetchCommissions(); fetchSummary(); }, [page, filter]);

  const fetchCommissions = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter) params.append('type', filter);
      const res = await axios.get(`${API}/commissions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setCommissions(res.data.commissions);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setLoading(false); }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await axios.get(`${API}/commissions/summary`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setSummary(res.data);
    } catch (e) { console.error('Commissions fetch error:', e); }
  };

  const typeConfig = {
    matrix: { label: t('Matrice', 'Matrix'), color: '#6366F1', bg: 'bg-indigo-500/10 border-indigo-500/20' },
    fast_start: { label: 'Fast Start', color: '#10B981', bg: 'bg-emerald-500/10 border-emerald-500/20' },
    matching: { label: 'Matching', color: '#8B5CF6', bg: 'bg-purple-500/10 border-purple-500/20' },
  };

  if (loading) {
    return (
      <OrbitLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </OrbitLayout>
    );
  }

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-commissions-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Commissions</h1>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['matrix', 'fast_start', 'matching'].map(type => {
            const cfg = typeConfig[type];
            const data = summary?.by_type?.[type] || { total: 0, count: 0 };
            return (
              <div key={type} className={`border rounded-xl p-5 ${cfg.bg}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cfg.color }} />
                  <span className="text-sm text-gray-200">{cfg.label}</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatMoney(data.total, language)}</p>
                <p className="text-xs text-gray-400 mt-1">{data.count} {t('transactions', 'transactions')}</p>
              </div>
            );
          })}
        </div>

        {/* By Orbit Breakdown */}
        {summary?.by_orbit && Object.keys(summary.by_orbit).length > 0 && (
          <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white mb-4">{t('Par orbite', 'By Orbit')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(summary.by_orbit).sort(([a], [b]) => Number(a) - Number(b)).map(([orbit, data]) => (
                <div key={orbit} className="bg-[#1E293B]/60 rounded-lg p-3 text-center">
                  <p className="text-indigo-300 text-xs">{t('Orbite', 'Orbit')} {orbit}</p>
                  <p className="text-lg font-bold text-white mt-1">{formatMoney(data.total, language)}</p>
                  <p className="text-xs text-gray-400">{data.count}x</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter & Table */}
        <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl">
          <div className="flex items-center justify-between p-4 border-b border-indigo-500/10">
            <h2 className="text-lg font-semibold text-white">{t('Historique', 'History')} ({total})</h2>
            <div className="flex items-center gap-2">
              <Funnel className="w-4 h-4 text-gray-400" />
              <select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                className="bg-[#1E293B] border border-indigo-500/20 rounded-lg text-white text-sm px-3 py-1.5"
                data-testid="orbit-commission-filter">
                <option value="">{t('Tous', 'All')}</option>
                <option value="matrix">{t('Matrice', 'Matrix')}</option>
                <option value="fast_start">Fast Start</option>
                <option value="matching">Matching</option>
              </select>
            </div>
          </div>

          {commissions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('Aucune commission', 'No commissions')}</p>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-300 text-xs uppercase border-b border-indigo-500/10">
                    <tr>
                      <th className="px-4 py-3 text-left">{t('Type', 'Type')}</th>
                      <th className="px-4 py-3 text-left">{t('De', 'From')}</th>
                      <th className="px-4 py-3 text-center">{t('Orbite', 'Orbit')}</th>
                      <th className="px-4 py-3 text-right">{t('Montant', 'Amount')}</th>
                      <th className="px-4 py-3 text-right">{t('Date', 'Date')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-500/10">
                    {commissions.map((comm) => {
                      const cfg = typeConfig[comm.type] || typeConfig.matrix;
                      return (
                        <tr key={comm.commission_id || `${comm.from_user_id}-${comm.created_at}`} className="hover:bg-white/5 transition-colors">
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded text-xs" style={{ color: cfg.color, backgroundColor: cfg.color + '15' }}>
                              {cfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-300">{comm.from_user_name}</td>
                          <td className="px-4 py-3 text-center text-gray-400">{comm.orbit}</td>
                          <td className="px-4 py-3 text-right text-emerald-400 font-medium">+{formatMoney(comm.amount, language)}</td>
                          <td className="px-4 py-3 text-right text-gray-400">{new Date(comm.created_at).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-indigo-500/10">
                {commissions.map((comm) => {
                  const cfg = typeConfig[comm.type] || typeConfig.matrix;
                  return (
                    <div key={comm.commission_id || `m-${comm.from_user_id}-${comm.created_at}`} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <span className="px-2 py-0.5 rounded text-xs" style={{ color: cfg.color, backgroundColor: cfg.color + '15' }}>
                          {cfg.label}
                        </span>
                        <p className="text-white text-sm mt-1">{comm.from_user_name}</p>
                        <p className="text-gray-400 text-xs">{t('Orbite', 'Orbit')} {comm.orbit}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-400 font-medium">+{formatMoney(comm.amount, language)}</p>
                        <p className="text-gray-400 text-xs">{new Date(comm.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-indigo-500/10">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1.5 bg-[#1E293B] rounded-lg text-sm text-gray-300 disabled:opacity-30 hover:bg-white/10">
                    {t('Precedent', 'Previous')}
                  </button>
                  <span className="text-gray-300 text-sm">{page} / {pages}</span>
                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
                    className="px-3 py-1.5 bg-[#1E293B] rounded-lg text-sm text-gray-300 disabled:opacity-30 hover:bg-white/10">
                    {t('Suivant', 'Next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitCommissionsPage;
